import React, { useEffect, useState, useCallback } from 'react';
import { notifAPI, orderAPI} from './SellerApi';

// Auto-refresh every 30 seconds so seller sees new orders without page reload
const POLL_INTERVAL = 30_000;

const SellerNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null); // For Modal
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [marking,       setMarking]       = useState(false);

  // ── Fetch from backend ────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await notifAPI.list();
      if (res?.status) {
        setNotifications(res.data        || []);
        setUnreadCount  (res.unread_count ?? 0);
        setError(null);
      }
    } catch {
      setError('Could not load notifications. Check your connection.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(true);
    const timer = setInterval(() => fetchNotifications(false), POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  const handleNotifClick = async (notif) => {
    const orderId = notif.order_ref; // Found via image_a46848.png
    
    try {
      // Call the 'detail' method defined in your orderAPI
      const res = await orderAPI.detail(orderId); 
      if (res?.status) {
        setSelectedOrder(res.data);
      }
    } catch (err) {
      console.error(err);
      alert("Could not load order details.");
    }
  };

  // ── Mark all as read ──────────────────────────────────────────────────────
  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return;
    setMarking(true);
    try {
      await notifAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
      setUnreadCount(0);
    } catch {
      alert('Could not mark as read. Try again.');
    } finally {
      setMarking(false);
    }
  };

  // ── Render states ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="notifications-container">
        <div style={styles.center}>Loading notifications…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notifications-container">
        <div style={{ ...styles.center, color: '#e53e3e' }}>
          {error}
          <br />
          <button style={styles.retryBtn} onClick={() => fetchNotifications(true)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-container">

      {/* ── Header ── */}
      <div className="notif-header" style={styles.header}>
        <h3 style={styles.title}>
          Notifications
          {unreadCount > 0 && (
            <span style={styles.badge}>{unreadCount} new</span>
          )}
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            style={{ ...styles.refreshBtn }}
            onClick={() => fetchNotifications(false)}
            title="Refresh"
          >
            ↻
          </button>
          <button
            className="mark-read-btn"
            onClick={handleMarkAllRead}
            disabled={marking || unreadCount === 0}
          >
            {marking ? 'Marking…' : 'Mark all as read'}
          </button>
        </div>
      </div>

      {/* ── List ── */}
      <div className="notif-list">
        {notifications.length === 0 ? (
          <div style={styles.empty}>
            <span style={{ fontSize: 36 }}>🔔</span>
            <p>No notifications yet.</p>
            <p style={{ fontSize: 12, color: '#999' }}>
              New orders will appear here automatically.
            </p>
          </div>
        ) : (
          notifications.map(notif => (
            <div
              key={notif.id}
              onClick={() => handleNotifClick(notif)}
              className={`notif-item ${notif.type}`}
              style={{
                display:      'flex',
                alignItems:   'flex-start',
                gap:          12,
                padding:      '14px 16px',
                borderBottom: '1px solid #f0f0f0',
                background:   notif.unread ? '#f0fff4' : '#fff',
                borderLeft:   notif.unread ? '4px solid #1a5d1a' : '4px solid transparent',
                transition:   'background 0.2s',
              }}
            >
              <span style={{ fontSize: 22, lineHeight: 1 }}>{notif.icon || '🔔'}</span>
              <div style={{ flex: 1 }}>
                <p style={{
                  margin:     0,
                  fontWeight: notif.unread ? 600 : 400,
                  color:      '#1a202c',
                  fontSize:   14,
                }}>
                  {notif.text}
                </p>
                {notif.order_ref && (
                  <span style={{ fontSize: 11, color: '#1a5d1a', fontWeight: 500 }}>
                    {notif.order_ref}
                  </span>
                )}
                <p style={{ margin: '4px 0 0', fontSize: 11, color: '#888' }}>
                  {notif.time}
                </p>
              </div>
              {notif.unread && (
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#1a5d1a', flexShrink: 0, marginTop: 6,
                }} />
              )}
            </div>
          ))
        )}
      </div>

      {/* ── Order Detail Modal ── */}
      {selectedOrder && (
        <div style={styles.modalOverlay} onClick={() => setSelectedOrder(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3>Order Details: {selectedOrder.order_id}</h3>
            <p><strong>Date:</strong> {selectedOrder.created_at}</p>
            <p><strong>Total:</strong> INR {selectedOrder.total}</p>
            <hr />
            <h4>Items:</h4>
            <ul>
              {selectedOrder.items.map(item => (
                <li key={item.id}>{item.product_name} x {item.quantity} - INR {item.line_total}</li>
              ))}
            </ul>
            <button onClick={() => setSelectedOrder(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Inline styles ─────────────────────────────────────────────────────────────
const styles = {
  header: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    padding:        '16px 16px 12px',
    borderBottom:   '1px solid #e8e8e8',
  },
  title: { margin: 0, fontSize: 16, color: '#1a202c', display: 'flex', alignItems: 'center', gap: 8 },
  badge: {
    background: '#e53e3e', color: '#fff',
    borderRadius: 12, padding: '2px 8px', fontSize: 11,
  },
  refreshBtn: {
    background: 'none', border: '1px solid #e2e8f0',
    borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
    fontSize: 16, color: '#666',
  },
  center: { textAlign: 'center', padding: '40px 20px', color: '#888' },
  retryBtn: {
    marginTop: 12, padding: '6px 16px', borderRadius: 6,
    border: '1px solid #cbd5e0', cursor: 'pointer', background: '#fff',
  },
  empty: { textAlign: 'center', padding: '50px 20px', color: '#aaa' },
};

export default SellerNotifications;